import { defineStore } from 'pinia';
import {
  hypothesesApi,
  type Hypothesis,
  type HypothesisWithDetails,
  type CreateHypothesisDto,
  type SaveCanvasStateDto,
} from '@api/hypotheses.api';
import { useToast } from '../composables/useToast';
import { prefetchBlockImages } from '../composables/useBlockImageCache';

const MAX_UNDO = 50;

export const useHypothesesStore = defineStore('hypotheses', {
  state: () => ({
    hypotheses: [] as Hypothesis[],
    currentHypothesis: null as HypothesisWithDetails | null,
    loading: false,
    error: null as string | null,
    undoStack: [] as SaveCanvasStateDto[],
    redoStack: [] as SaveCanvasStateDto[],
  }),

  getters: {
    canUndo(state): boolean {
      return state.currentHypothesis != null && state.undoStack.length > 0;
    },
    canRedo(state): boolean {
      return state.currentHypothesis != null && state.redoStack.length > 0;
    },
  },

  actions: {
    getCurrentCanvasState(): SaveCanvasStateDto | null {
      if (!this.currentHypothesis) return null;
      return {
        blocks: (this.currentHypothesis.blocks ?? []).map((b) => ({
          bloc_id: b.bloc_id,
          position_x: b.position_x,
          position_y: b.position_y,
          rotation: b.rotation ?? 0,
          z_order: b.z_order ?? 0,
          locked: Boolean((b as any).locked),
        })),
        groups: this.currentHypothesis.groups ?? [],
      };
    },

    pushUndoSnapshot() {
      const state = this.getCurrentCanvasState();
      if (!state) return;
      this.undoStack.push(JSON.parse(JSON.stringify(state)));
      if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
      this.redoStack = [];
    },

    async undo() {
      if (!this.currentHypothesis || this.undoStack.length === 0) return;
      const current = this.getCurrentCanvasState();
      if (current) this.redoStack.push(JSON.parse(JSON.stringify(current)));
      const state = this.undoStack.pop()!;
      try {
        const updated = await hypothesesApi.saveCanvasState(this.currentHypothesis.id, state);
        this.currentHypothesis = updated;
      } catch (e) {
        this.redoStack.pop();
        this.undoStack.push(state);
        throw e;
      }
    },

    async redo() {
      if (!this.currentHypothesis || this.redoStack.length === 0) return;
      const current = this.getCurrentCanvasState();
      if (current) this.undoStack.push(JSON.parse(JSON.stringify(current)));
      const state = this.redoStack.pop()!;
      try {
        const updated = await hypothesesApi.saveCanvasState(this.currentHypothesis.id, state);
        this.currentHypothesis = updated;
      } catch (e) {
        this.undoStack.pop();
        this.redoStack.push(state);
        throw e;
      }
    },

    updateBlockLocally(blockId: number, updates: any) {
      if (!this.currentHypothesis) return;
      const blockIndex = this.currentHypothesis.blocks.findIndex((b) => b.id === blockId);
      if (blockIndex !== -1) {
        this.currentHypothesis.blocks[blockIndex] = {
          ...this.currentHypothesis.blocks[blockIndex],
          ...updates,
        };
      }
    },

    updateBlocksLocally(updates: { blockId: number; updates: any }[]) {
      if (!this.currentHypothesis) return;
      for (const { blockId, updates: u } of updates) {
        const blockIndex = this.currentHypothesis.blocks.findIndex((b) => b.id === blockId);
        if (blockIndex !== -1) {
          this.currentHypothesis.blocks[blockIndex] = {
            ...this.currentHypothesis.blocks[blockIndex],
            ...u,
          };
        }
      }
    },

    setGroupsLocally(groups: import('@api/hypotheses.api').CanvasGroup[]) {
      if (!this.currentHypothesis) return;
      this.currentHypothesis.groups = groups;
    },

    async fetchAll() {
      this.loading = true;
      this.error = null;
      try {
        this.hypotheses = await hypothesesApi.getAll();
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors du chargement des hypothèses';
        this.error = message;
        if (import.meta.env.DEV) {
          console.error('[HypothesesStore] Error fetching hypotheses:', error);
        }
      } finally {
        this.loading = false;
      }
    },

    async loadHypothesis(id: number) {
      this.loading = true;
      this.error = null;
      try {
        const prevId = this.currentHypothesis?.id;
        this.currentHypothesis = await hypothesesApi.getById(id);
        if (prevId != null && prevId !== id) {
          this.undoStack = [];
          this.redoStack = [];
        }

        // Préchargement en arrière-plan des images pleine résolution des blocs
        const blocsToPreload = (this.currentHypothesis.blocks ?? [])
          .map((b) => b.bloc)
          .filter(Boolean)
          .map((bloc) => ({ image_path: bloc.image_path, updated_at: bloc.updated_at ?? '' }));
        prefetchBlockImages(blocsToPreload).catch(() => {});
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors du chargement de l\'hypothèse';
        this.error = message;
        if (import.meta.env.DEV) {
          console.error('[HypothesesStore] Error loading hypothesis:', error);
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async create(data: CreateHypothesisDto) {
      this.loading = true;
      this.error = null;
      try {
        const newHypothesis = await hypothesesApi.create(data);
        this.hypotheses.push(newHypothesis);
        return newHypothesis;
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors de la création de l\'hypothèse';
        this.error = message;
        if (import.meta.env.DEV) {
          console.error('[HypothesesStore] Error creating hypothesis:', error);
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async update(id: number, data: Partial<CreateHypothesisDto>) {
      this.loading = true;
      this.error = null;
      try {
        const updatedHypothesis = await hypothesesApi.update(id, data);
        const index = this.hypotheses.findIndex((h) => h.id === id);
        if (index !== -1) {
          this.hypotheses[index] = updatedHypothesis;
        }
        if (this.currentHypothesis?.id === id) {
          this.currentHypothesis = { ...this.currentHypothesis, ...updatedHypothesis };
        }
        return updatedHypothesis;
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors de la mise à jour de l\'hypothèse';
        this.error = message;
        if (import.meta.env.DEV) {
          console.error('[HypothesesStore] Error updating hypothesis:', error);
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async saveCanvasState(id: number, state: SaveCanvasStateDto) {
      this.loading = true;
      this.error = null;
      const { error: showError } = useToast();
      try {
        const updated = await hypothesesApi.saveCanvasState(id, state);
        this.currentHypothesis = updated;
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors de la sauvegarde';
        this.error = message;
        showError(message);
        if (import.meta.env.DEV) {
          console.error('[HypothesesStore] Error saving canvas state:', error);
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async duplicate(id: number, name?: string): Promise<HypothesisWithDetails> {
      this.error = null;
      try {
        const created = await hypothesesApi.duplicate(id, name);
        this.hypotheses.push({
          id: created.id,
          name: created.name,
          comment: created.comment,
          created_at: created.created_at,
          updated_at: created.updated_at,
        });
        return created;
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors de la duplication de l\'hypothèse';
        this.error = message;
        if (import.meta.env.DEV) {
          console.error('[HypothesesStore] Error duplicating hypothesis:', error);
        }
        throw error;
      }
    },

    async delete(id: number) {
      this.loading = true;
      this.error = null;
      try {
        await hypothesesApi.delete(id);
        this.hypotheses = this.hypotheses.filter((h) => h.id !== id);
        if (this.currentHypothesis?.id === id) {
          this.currentHypothesis = null;
        }
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors de la suppression de l\'hypothèse';
        this.error = message;
        if (import.meta.env.DEV) {
          console.error('[HypothesesStore] Error deleting hypothesis:', error);
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    setCurrentHypothesis(hypothesis: HypothesisWithDetails | null) {
      this.currentHypothesis = hypothesis;
    },
  },
});
